import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export interface ThroughputPoint {
  date: string;
  count: number;
}

export type ThroughputTier = "hot" | "cold";
export type ThroughputBucket = "day" | "week" | "month";

export interface ThroughputData {
  data: ThroughputPoint[];
  tier?: ThroughputTier;
  bucket?: ThroughputBucket;
}

export function useThroughput(
  jobTypeId?: string,
  from?: string,
  to?: string,
  bucket?: ThroughputBucket,
) {
  const [data, setData] = useState<ThroughputData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (jobTypeId) params.set("jobTypeId", jobTypeId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = params.toString() ? `?${params.toString()}` : "";
      const result = await api.get<ThroughputData>(`/analytics/throughput${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load throughput data");
    } finally {
      setIsLoading(false);
    }
  }, [jobTypeId, from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
